// Canvas renderer. Two layers:
//   1. Terrain — cached in a 4×4 grid of offscreen chunk canvases painted at
//      source-art scale (16 px/tile, so sprite draws are 1:1). Chunks are
//      lazily allocated + painted on first viewport intersection and evicted
//      LRU beyond MAX_RESIDENT_CHUNKS. Snapshot terrain deltas arrive via
//      ClientState.terrainDirty (appended by applySnapshot, drained here) so
//      steady-state frames repaint only the changed tiles' 3×3 patches — no
//      full-map scan. Visible chunks are blitted to the main canvas each
//      frame with a nearest-neighbour 2× upscale (16 px art → TILE_PX=32).
//   2. Entities — tanks, shells, pillboxes (owner-tinted), bases, LGMs —
//      cleared and redrawn each frame on top of the terrain blit.

import {
  GUNSIGHT_MAX,
  MAP_SIZE,
  Terrain,
  TERRAIN_MASK,
  TERRAIN_MINE,
  WORLD_UNITS_PER_TILE,
  type BotPersonality,
} from '@bolo2k/shared';
import {
  LGM_COLOR,
  SHELL_COLOR,
  ownerColor,
  terrainColor,
} from './colors.js';
import { LGMStatus, dirVector, type Tank } from '@bolo2k/sim';
import {
  CHUNK_COUNT,
  CHUNK_TILES,
  CHUNKS_PER_SIDE,
  ChunkLRU,
  MAX_RESIDENT_CHUNKS,
  chunkOfTile,
  chunkOriginTiles,
  forEachNeighborTile,
} from './chunkGrid.js';
import { isFriendlyToMe, type ClientBase, type ClientPillbox, type ClientShell, type ClientState, type ClientTank } from './clientState.js';
import type { Interpolator } from './interpolator.js';
import {
  ART_TILE_SIZE,
  drawBaseSprite,
  drawBoat as drawBoatSprite,
  drawExplosionSprite,
  drawGunsightSprite,
  drawHelicopterSprite,
  drawLgmSprite,
  drawPillboxSprite,
  drawShellSprite,
  drawTank as drawTankSprite,
  drawTile as drawTerrainTile,
  getSprites,
} from './sprites.js';

// Tank-explosion ttl ceiling (TANK_EXPLOSION_TTL_TICKS in sim/shared); used
// to convert ttl into 0..1 progress for explosion-frame selection.
const TANK_EXPLOSION_TTL = 40;

// On-screen pixels per BMAP tile. Sprites are 16 px so we want a clean
// integer multiple — 32 = 2×, 48 = 3×. 32 keeps roughly the previous on-screen
// scale and gives crisp pixel-art with imageSmoothingEnabled off.
export const TILE_PX = 32;

// Chunk canvases are painted at source-art scale (16 px/tile) and blitted to
// the main canvas at TILE_PX with a nearest-neighbour integer upscale. With
// TILE_PX=32 that's an exact 2× — every 16-px source pixel maps to a 2×2
// on-screen block, pixel-identical to the previous pipeline that pre-scaled
// tiles to 32 px inside the terrain cache.
const SRC_SCALE = TILE_PX / ART_TILE_SIZE; // 2
const CHUNK_SRC_PX = CHUNK_TILES * ART_TILE_SIZE; // 1024 — chunk canvas size
const CHUNK_SCREEN_PX = CHUNK_TILES * TILE_PX; // 2048 — chunk span on screen

// One entry of the renderer's chunked terrain cache.
interface TerrainChunk {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  /** false = contents stale (fresh allocation, invalidateTerrainCache(), or
   *  new map). Painted lazily on first viewport intersection. */
  painted: boolean;
}

export class Renderer {
  // Chunked terrain cache, indexed by chunk index (row-major, see
  // chunkGrid.ts). null = not resident (never allocated, or LRU-evicted).
  private chunks: (TerrainChunk | null)[];
  private chunkLru = new ChunkLRU(MAX_RESIDENT_CHUNKS);
  // Identity of the terrain array we're caching. A welcome creates a fresh
  // Uint8Array (initFromWelcome), so an identity change means "new map" —
  // full invalidate, no per-tile diffing needed.
  private terrainRef: Uint8Array | null = null;
  private mainCtx: CanvasRenderingContext2D;
  private camX = 0; // viewport top-left in tile coords (fractional)
  private camY = 0;
  // Snapped camera origin in source-canvas pixels, recomputed once per draw().
  // The terrain blit uses these so the source rect is always pixel-aligned —
  // fractional source coords on a Retina (DPR>1) blit with imageSmoothingEnabled
  // off causes nearest-neighbour sampling to alternate between adjacent source
  // pixels as the camera advances sub-pixel per frame, which reads as strobing
  // on high-frequency textures like grass and forest. Entities keep their
  // floating-point positions but are computed relative to this snapped origin
  // so they don't drift against the terrain.
  private camPxX = 0;
  private camPxY = 0;
  // Local gunsight visibility preference, toggled by the G hotkey. When true,
  // the gunsight is hidden whenever the player's sightLen is at GUNSIGHT_MAX
  // — mirrors WinBolo's `autoHideGunsight` preference (tank.c:731-762). The
  // gunsight is otherwise always shown for the local player. Persisted by
  // main.ts via localStorage; the renderer just reads the current value.
  hideGunsightAtMax = false;
  // Viewport dimensions in CSS pixels — cached on resize() so the per-frame
  // draw path doesn't read canvas.clientWidth/clientHeight (each read forces
  // a synchronous layout). Backing-store dimensions and DPR also cached so
  // we can skip reassigning canvas.width/height when nothing actually
  // changed; reassignment clears the canvas and was the source of visible
  // flicker during fast camera movement when ResizeObserver fired on
  // sub-pixel layout adjustments.
  private viewW = 0;
  private viewH = 0;
  private bufW = 0;
  private bufH = 0;
  private bufRatio = 0;

  constructor(private mainCanvas: HTMLCanvasElement) {
    const ctx = mainCanvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.mainCtx = ctx;
    this.mainCtx.imageSmoothingEnabled = false;
    this.chunks = new Array<TerrainChunk | null>(CHUNK_COUNT).fill(null);
  }

  // Call after sprite assets finish loading — invalidates the terrain cache
  // so visible chunks repaint with real sprites on the next frame (and
  // off-screen chunks lazily, on their next viewport intersection).
  invalidateTerrainCache(): void {
    for (const chunk of this.chunks) {
      if (chunk) chunk.painted = false;
    }
  }

  cameraTile(): { x: number; y: number } {
    return { x: this.camX, y: this.camY };
  }

  resize(width: number, height: number): void {
    const ratio = window.devicePixelRatio || 1;
    // Allocate +1 source pixel of slack on the right and bottom — the canvas
    // is sized via CSS to calc(100% + 1px) so the draw() loop can apply the
    // sub-pixel camera residual as a CSS `translate(-fracX, -fracY)` without
    // revealing the panel background underneath. The wrapper #stage-clip
    // crops the overage.
    const canvasW = width + 1;
    const canvasH = height + 1;
    const wPx = Math.floor(canvasW * ratio);
    const hPx = Math.floor(canvasH * ratio);
    // Reassigning canvas.width/height clears the backing store (and ALL 2D
    // context state, including the transform). ResizeObserver fires for
    // every layout pass — including fractional-pixel adjustments that don't
    // change the integer backing-store size — so guard the reassignment to
    // avoid per-frame canvas wipes that show up as visible flicker.
    if (wPx !== this.bufW || hPx !== this.bufH || ratio !== this.bufRatio) {
      this.mainCanvas.width = wPx;
      this.mainCanvas.height = hPx;
      this.mainCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.mainCtx.imageSmoothingEnabled = false;
      this.bufW = wPx;
      this.bufH = hPx;
      this.bufRatio = ratio;
    }
    // CSS dimensions of the canvas are managed by the stylesheet (calc(100% +
    // 1px) of the wrapper) — assigning them here would override that. viewW/H
    // track the *visible* wrapper area; the +1 overage lives only in the
    // backing store + draw rect below.
    this.viewW = width;
    this.viewH = height;
  }

  draw(
    state: ClientState,
    interp?: Interpolator,
    overlay?: (ctx: CanvasRenderingContext2D) => void,
    predictedMe?: Tank | null,
    spectatorCentre?: { x: number; y: number } | null,
  ): void {
    this.syncTerrain(state);
    this.centreCameraOnPlayer(state, interp, predictedMe, spectatorCentre ?? null);
    // Snap the source rect to integer source pixels (avoids the sub-pixel
    // nearest-neighbour sampling artifact that strobes high-frequency tile
    // textures). Use floor so the residual is always in [0, 1) — the canvas
    // then slides LEFT by `frac` CSS pixels, leaving its +1-pixel right/bottom
    // slack inside the wrapper.
    const camWX = this.camX * TILE_PX;
    const camWY = this.camY * TILE_PX;
    this.camPxX = Math.floor(camWX);
    this.camPxY = Math.floor(camWY);
    const fracX = camWX - this.camPxX;
    const fracY = camWY - this.camPxY;
    // Apply the residual via CSS transform — the GPU compositor slides the
    // canvas at sub-pixel granularity, eliminating the integer-pixel cadence
    // strobing on grass/forest/swamp textures during fast scrolling. translate3d
    // forces a compositor layer so this stays off the layout/paint paths.
    this.mainCanvas.style.transform = `translate3d(${-fracX}px, ${-fracY}px, 0)`;
    this.blitTerrain(state);
    this.drawEntities(state, interp, predictedMe);
    if (overlay) overlay(this.mainCtx);
  }

  // Delta-driven terrain sync. Drains ClientState.terrainDirty (appended by
  // applySnapshot — this renderer is the single consumer, see the field's
  // contract) and repaints only the changed tiles' 3×3 neighbourhoods in
  // whichever resident chunks they touch. The neighbour-aware tile picker
  // means a change at (x, y) can also change the *visual* of all 8
  // surrounding tiles — even though their bytes didn't change — so the whole
  // patch repaints, crossing chunk borders where needed. No full-map scan on
  // any steady-state frame.
  private syncTerrain(state: ClientState): void {
    if (state.terrain !== this.terrainRef) {
      // New map — initFromWelcome allocates a fresh terrain array, so an
      // identity change means everything we cached is for the wrong world.
      // Mark all chunks stale; visible ones repaint lazily in blitTerrain.
      this.terrainRef = state.terrain;
      state.terrainDirty.length = 0; // the full repaint subsumes pending deltas
      this.invalidateTerrainCache();
      return;
    }
    if (state.terrainDirty.length === 0) return;
    for (const tileIndex of state.terrainDirty) {
      forEachNeighborTile(tileIndex, (x, y) => {
        const chunk = this.chunks[chunkOfTile(x, y)];
        // Non-resident or stale chunks need no per-tile work — they repaint
        // in full (from the current terrain) on next viewport intersection.
        if (!chunk || !chunk.painted) return;
        this.paintTileIntoChunk(chunk, x, y, state.terrain);
      });
    }
    state.terrainDirty.length = 0;
  }

  // Paint one tile at source-art scale (16 px — sprite draws are 1:1, no
  // scaling) into the chunk that owns it. Chunk borders align to
  // CHUNK_TILES, so tile-local coords are simple modulos.
  private paintTileIntoChunk(chunk: TerrainChunk, x: number, y: number, terrain: Uint8Array): void {
    const px = (x % CHUNK_TILES) * ART_TILE_SIZE;
    const py = (y % CHUNK_TILES) * ART_TILE_SIZE;
    const byte = terrain[y * MAP_SIZE + x]!;
    const code = byte & TERRAIN_MASK;
    const sprites = getSprites();
    if (sprites) {
      drawTerrainTile(chunk.ctx, sprites, terrain, x, y, code, px, py, ART_TILE_SIZE, (byte & TERRAIN_MINE) !== 0);
    } else {
      chunk.ctx.fillStyle = terrainColor(code);
      chunk.ctx.fillRect(px, py, ART_TILE_SIZE, ART_TILE_SIZE);
      if ((byte & TERRAIN_MINE) !== 0) {
        chunk.ctx.fillStyle = 'rgba(248,81,73,0.55)';
        // Half the old 32-px-scale 4×4 dot at (+2,+2) — the 2× blit restores
        // the identical on-screen footprint.
        chunk.ctx.fillRect(px + 1, py + 1, 2, 2);
      }
    }
  }

  // Return the chunk for `chunkIndex`, allocating and/or painting it if
  // needed, and refresh its LRU recency. Eviction drops the least-recently
  // blitted chunks' canvases entirely — they reallocate + repaint on their
  // next viewport intersection.
  private ensureChunkPainted(chunkIndex: number, terrain: Uint8Array): TerrainChunk {
    let chunk = this.chunks[chunkIndex] ?? null;
    if (!chunk) {
      const canvas = document.createElement('canvas');
      canvas.width = CHUNK_SRC_PX;
      canvas.height = CHUNK_SRC_PX;
      const cCtx = canvas.getContext('2d');
      if (!cCtx) throw new Error('offscreen chunk canvas 2d context unavailable');
      // Smoothing off for parity with the old terrain canvas. Tile paints
      // are 1:1 (no scaling) so this is belt-and-braces; the crispness of
      // the 2× upscale is governed by mainCtx's imageSmoothingEnabled=false.
      cCtx.imageSmoothingEnabled = false;
      chunk = { canvas, ctx: cCtx, painted: false };
      this.chunks[chunkIndex] = chunk;
    }
    for (const evicted of this.chunkLru.touch(chunkIndex)) {
      this.chunks[evicted] = null;
    }
    if (!chunk.painted) {
      this.paintChunk(chunkIndex, chunk, terrain);
      chunk.painted = true;
    }
    return chunk;
  }

  // Full 64×64-tile chunk paint (~4,096 tiles). Runs only on first viewport
  // intersection after allocation/invalidation — the visible set (≤4 chunks
  // typically) covers a worst case of ~16k tiles on the first frame after a
  // welcome, in place of the old eager 65,536-tile full-map paint.
  private paintChunk(chunkIndex: number, chunk: TerrainChunk, terrain: Uint8Array): void {
    const { tileX: ox, tileY: oy } = chunkOriginTiles(chunkIndex);
    for (let ty = 0; ty < CHUNK_TILES; ty++) {
      for (let tx = 0; tx < CHUNK_TILES; tx++) {
        this.paintTileIntoChunk(chunk, ox + tx, oy + ty, terrain);
      }
    }
  }

  private centreCameraOnPlayer(
    state: ClientState,
    interp?: Interpolator,
    predictedMe?: Tank | null,
    cameraOverride?: { x: number; y: number } | null,
  ): void {
    const me = state.tanks.find((t) => t.id === state.myTankId);
    const viewW = this.viewW;
    const viewH = this.viewH;
    if (cameraOverride) {
      // Override wins — used by both spectator free-pan (WASD-driven, see
      // main.ts spectatorCam) and pillview (camera centred on a friendly
      // pillbox). Coordinates are in tile units.
      this.camX = cameraOverride.x - viewW / (2 * TILE_PX);
      this.camY = cameraOverride.y - viewH / (2 * TILE_PX);
    } else if (me) {
      // Prefer the predicted position for the local player so the camera
      // tracks input with zero round-trip latency.
      let wx: number;
      let wy: number;
      if (predictedMe) {
        wx = predictedMe.x;
        wy = predictedMe.y;
      } else {
        const lerped = interp?.lerpTank(me.id, performance.now());
        wx = lerped?.x ?? me.x;
        wy = lerped?.y ?? me.y;
      }
      const tileX = wx / WORLD_UNITS_PER_TILE;
      const tileY = wy / WORLD_UNITS_PER_TILE;
      this.camX = tileX - viewW / (2 * TILE_PX);
      this.camY = tileY - viewH / (2 * TILE_PX);
    } else {
      // No tank, no override yet — centre on map.
      this.camX = MAP_SIZE / 2 - viewW / (2 * TILE_PX);
      this.camY = MAP_SIZE / 2 - viewH / (2 * TILE_PX);
    }
    // Clamp camera within the map. Account for the +1 screen-pixel overage
    // the terrain blit draws (see blitTerrain): if camX hit the right edge
    // precisely, the extra column would otherwise fall past the last chunk
    // and leave an unpainted strip there.
    this.camX = Math.max(0, Math.min(MAP_SIZE - (viewW + 1) / TILE_PX, this.camX));
    this.camY = Math.max(0, Math.min(MAP_SIZE - (viewH + 1) / TILE_PX, this.camY));
  }

  private blitTerrain(state: ClientState): void {
    // +1 screen pixel of overage on the right and bottom — paired with the
    // canvas's CSS `calc(100% + 1px)` slack and the sub-pixel CSS transform
    // applied in draw(). When fracX/Y > 0, the canvas slides leftward, and
    // this extra column/row is what fills the wrapper's right/bottom edge.
    // The residual stays in [0, 1) — camPxX/Y are still snapped to integer
    // *screen* pixels (TILE_PX units), so the slack math is unchanged from
    // the single-canvas pipeline.
    const drawW = this.viewW + 1;
    const drawH = this.viewH + 1;
    // Chunk range intersecting the viewport (≤4 chunks typically; up to 9 on
    // very large windows).
    const c0x = Math.max(0, Math.floor(this.camPxX / CHUNK_SCREEN_PX));
    const c0y = Math.max(0, Math.floor(this.camPxY / CHUNK_SCREEN_PX));
    const c1x = Math.min(CHUNKS_PER_SIDE - 1, Math.floor((this.camPxX + drawW - 1) / CHUNK_SCREEN_PX));
    const c1y = Math.min(CHUNKS_PER_SIDE - 1, Math.floor((this.camPxY + drawH - 1) / CHUNK_SCREEN_PX));
    // No pre-clear — the camera is clamped to the map, so the chunk blits
    // fully cover the viewport. A black fill before drawImage would just
    // briefly show black between frames during heavy paint operations.
    for (let cy = c0y; cy <= c1y; cy++) {
      for (let cx = c0x; cx <= c1x; cx++) {
        const chunk = this.ensureChunkPainted(cy * CHUNKS_PER_SIDE + cx, state.terrain);
        const originX = cx * CHUNK_SCREEN_PX; // chunk origin in screen-space px
        const originY = cy * CHUNK_SCREEN_PX;
        // Visible sub-rect of this chunk, snapped OUTWARD to integer source
        // pixels (16-px art units). Integer source coords + integer dest
        // coords keep the nearest-neighbour 2× upscale an exact pixel
        // duplication — fractional source coords on a DPR>1 blit with
        // smoothing off cause the strobing documented at camPxX/camPxY.
        const sx0 = Math.max(0, Math.floor((this.camPxX - originX) / SRC_SCALE));
        const sy0 = Math.max(0, Math.floor((this.camPxY - originY) / SRC_SCALE));
        const sx1 = Math.min(CHUNK_SRC_PX, Math.ceil((this.camPxX + drawW - originX) / SRC_SCALE));
        const sy1 = Math.min(CHUNK_SRC_PX, Math.ceil((this.camPxY + drawH - originY) / SRC_SCALE));
        if (sx1 <= sx0 || sy1 <= sy0) continue;
        // Dest anchored so source pixel sx0 lands exactly where the old
        // single-canvas blit put it; the outward snap can start ≤1 screen px
        // left/above the viewport (the canvas clips it).
        const dx = originX + sx0 * SRC_SCALE - this.camPxX;
        const dy = originY + sy0 * SRC_SCALE - this.camPxY;
        this.mainCtx.drawImage(
          chunk.canvas,
          sx0,
          sy0,
          sx1 - sx0,
          sy1 - sy0,
          dx,
          dy,
          (sx1 - sx0) * SRC_SCALE,
          (sy1 - sy0) * SRC_SCALE,
        );
      }
    }
  }

  private worldToScreen(wx: number, wy: number): { sx: number; sy: number } {
    const tx = wx / WORLD_UNITS_PER_TILE;
    const ty = wy / WORLD_UNITS_PER_TILE;
    // Use the snapped camera origin (camPxX/camPxY) — keeps entities aligned
    // with the integer-pixel terrain blit while preserving their floating-point
    // sub-pixel motion relative to the world.
    return { sx: tx * TILE_PX - this.camPxX, sy: ty * TILE_PX - this.camPxY };
  }

  private drawEntities(state: ClientState, interp?: Interpolator, predictedMe?: Tank | null): void {
    const now = performance.now();
    // Pillbox + base owner tint (these don't move, no interp needed).
    // Carried pills stay in state.pillboxes so they keep their status-grid
    // slot, but they aren't on the map — skip world-rendering them.
    for (const p of state.pillboxes) {
      if (p.carriedBy !== 0) continue;
      this.drawPillbox(p, state);
    }
    for (const b of state.bases) {
      this.drawBase(b, state);
    }
    // Boats — the WinBolo boat sprite already includes the tank, so we draw
    // boats first and skip the tank for any rider currently aboard.
    const ownerByTankId = new Map<number, number>();
    for (const tank of state.tanks) ownerByTankId.set(tank.id, tank.owner);
    for (const boat of state.boats) {
      const owner = ownerByTankId.get(boat.riderTankId) ?? 0;
      // Local-player boat: use the predictor's shadow so the boat sprite
      // and the camera stay in lockstep (the predictor advances at 50 Hz
      // with sub-tick interpolation, and the camera reads from the same
      // shadow). Falling back to snapshot interpolation here would freeze
      // the boat sprite at the latest snapshot whenever delivery jittered,
      // producing the ~1-per-second jerk users reported at full speed.
      // Remote boats keep using the snapshot interpolator since we have
      // no prediction signal for other players' inputs.
      let drawX: number;
      let drawY: number;
      let drawDir: number;
      if (boat.riderTankId === state.myTankId && predictedMe) {
        drawX = predictedMe.x;
        drawY = predictedMe.y;
        drawDir = predictedMe.dir;
      } else {
        const lerped = interp?.lerpTank(boat.riderTankId, now);
        drawX = lerped?.x ?? boat.x;
        drawY = lerped?.y ?? boat.y;
        drawDir = lerped?.dir ?? boat.dir;
      }
      this.drawBoat({ x: drawX, y: drawY, dir: drawDir }, owner);
    }
    for (const tank of state.tanks) {
      if (!tank.alive) continue;
      if (tank.inBoatId !== 0) continue; // boat sprite covers this rider
      const personality = state.bots.get(tank.id);
      if (tank.id === state.myTankId && predictedMe) {
        // Position + direction from the predictor (50 Hz tick rate, sub-tick
        // interpolated to render frame rate). Reading tank.dir from the
        // snapshot looks correct at first — and was the old approach — but
        // updates only at the snapshot rate, so a player turning quickly
        // sees their tank's facing snap in ~40 ms steps. Predictor dir
        // converges to server dir within a few snapshots (50 % easing per
        // reconcile) and the 16-step sprite snap absorbs the brad-or-two
        // divergence, so shells still appear to leave along the visible
        // barrel direction.
        this.drawTank(tank, personality, { x: predictedMe.x, y: predictedMe.y, dir: predictedMe.dir });
      } else {
        const lerped = interp?.lerpTank(tank.id, now);
        this.drawTank(tank, personality, lerped);
      }
    }
    for (const shell of state.shells) {
      const lerped = interp?.lerpShell(shell.id, now);
      this.drawShell(shell, lerped);
    }
    // LGMs that are out of their tank — drawn either as a walking man or, if
    // a fresh LGM is being parachuted in after death, as an inbound helicopter.
    for (const tank of state.tanks) {
      const status = tank.lgmStatus;
      if (status === LGMStatus.InTank || status === LGMStatus.Dead) continue;
      const lerped = interp?.lerpLgm(tank.id, now);
      this.drawLGM(tank, lerped);
    }
    // Tank-death explosions on top of everything else.
    for (const e of state.tankExplosions) {
      this.drawTankExplosion(e);
    }
    // Local player's gunsight, on top of explosions so the player can still
    // aim through smoke. Skip if no tank yet or tank is dead. Direction
    // matches the tank's visible barrel — we use predictedMe.dir for both
    // (and fall back to server dir for the brief frames between welcome and
    // first predictor tick).
    const me = state.tanks.find((t) => t.id === state.myTankId);
    if (me && me.alive) {
      // Auto-hide at max — WinBolo `autoHideGunsight` preference. Skips the
      // draw entirely so the player sees no reticle while their range is
      // maxed; decreasing range below GUNSIGHT_MAX brings it back next
      // frame. Default GUNSIGHT_DEFAULT is GUNSIGHT_MAX, so toggling on
      // hides the reticle until the player actively dials range down.
      const hideForMax = this.hideGunsightAtMax && me.sightLen >= GUNSIGHT_MAX;
      if (!hideForMax) {
        const aim = predictedMe ?? (interp?.lerpTank(me.id, now) ?? me);
        const aimDir = predictedMe ? predictedMe.dir : me.dir;
        this.drawGunsight(aim.x, aim.y, aimDir, me.sightLen);
      }
    }
  }

  private drawTankExplosion(e: ClientState['tankExplosions'][number]): void {
    const { sx, sy } = this.worldToScreen(e.x, e.y);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    const big = e.kind === 'big';
    // ttl starts at TANK_EXPLOSION_TTL and counts down to 0; progress goes 0→1.
    const progress = 1 - e.ttl / TANK_EXPLOSION_TTL;
    if (sprites) {
      drawExplosionSprite(ctx, sprites, progress, big, sx, sy, TILE_PX);
      return;
    }
    // Fallback: synthesized radial flash (used briefly before sprites load).
    const baseRadius = big ? TILE_PX * 1.6 : TILE_PX * 0.6;
    const radius = baseRadius * (0.4 + 0.9 * progress);
    const alpha = Math.max(0, 1 - progress);
    const grad = ctx.createRadialGradient(sx, sy, radius * 0.1, sx, sy, radius);
    if (big) {
      grad.addColorStop(0, `rgba(255, 240, 200, ${alpha})`);
      grad.addColorStop(0.4, `rgba(255, 140, 40, ${alpha * 0.85})`);
      grad.addColorStop(1, `rgba(120, 30, 10, 0)`);
    } else {
      grad.addColorStop(0, `rgba(255, 230, 180, ${alpha})`);
      grad.addColorStop(1, `rgba(180, 80, 40, 0)`);
    }
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawTank(
    tank: ClientTank,
    personality: BotPersonality | undefined,
    lerped?: { x: number; y: number; dir: number } | null,
  ): void {
    const isBot = personality !== undefined;
    const wx = lerped?.x ?? tank.x;
    const wy = lerped?.y ?? tank.y;
    const dir = lerped?.dir ?? tank.dir;
    const { sx, sy } = this.worldToScreen(wx, wy);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    const size = TILE_PX;

    if (sprites) {
      drawTankSprite(ctx, sprites, tank.owner, dir, sx, sy, size);
      // Original Bolo identifies the local player by their assigned tank
      // colour (one of 8/16 hues). No "find yourself" highlight ring — the
      // sprite tint is the identity. Match that.
      if (isBot) drawBotBadge(ctx, sx, sy - size * 0.65, personality);
      return;
    }

    // Fallback: synthesized rotated tank (kept around for the brief moment
    // before /sprites/*.png finish loading).
    const half = TILE_PX * 0.45;
    const angleRad = (dir / 256) * Math.PI * 2;
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(angleRad);
    ctx.fillStyle = ownerColor(tank.owner);
    ctx.fillRect(-half, -half, half * 2, half * 2);
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.lineWidth = 1;
    ctx.strokeRect(-half + 0.5, -half + 0.5, half * 2 - 1, half * 2 - 1);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.beginPath();
    ctx.moveTo(-half * 0.7, -half + half * 0.05);
    ctx.lineTo(half * 0.7, -half + half * 0.05);
    ctx.lineTo(0, -half - half * 0.55);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -half * 1.7);
    ctx.stroke();
    ctx.restore();
    if (isBot) drawBotBadge(ctx, sx, sy - size * 0.65, personality);
  }

  private drawShell(shell: ClientShell, lerped?: { x: number; y: number } | null): void {
    const { sx, sy } = this.worldToScreen(lerped?.x ?? shell.x, lerped?.y ?? shell.y);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    if (sprites) {
      drawShellSprite(ctx, sprites, shell.dir, sx, sy, TILE_PX);
      return;
    }
    ctx.fillStyle = SHELL_COLOR;
    ctx.beginPath();
    ctx.arc(sx, sy, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawPillbox(p: ClientPillbox, state: ClientState): void {
    const { sx, sy } = this.worldToScreen(
      (p.x << 8) + (WORLD_UNITS_PER_TILE >> 1),
      (p.y << 8) + (WORLD_UNITS_PER_TILE >> 1),
    );
    const ctx = this.mainCtx;
    const sprites = getSprites();
    // Alliance tint across the full armour range (0..15). Matches WinBolo
    // pillsGetScreenHealth (pillbox.c:472) — the armour=0 hollow-shell sprite
    // is alliance-coloured just like the damaged-but-functional cells. Old
    // behaviour forced grey at armour=0 on the theory that a destroyed pill
    // reads as "neutral pickup"; the WinBolo audit (DECISIONS.md history)
    // showed that was an unverified guess about Mac Bolo's behaviour, so we
    // matched WinBolo instead.
    const kind: 'good' | 'evil' = isFriendlyToMe(state, p.owner) ? 'good' : 'evil';
    if (sprites) {
      drawPillboxSprite(ctx, sprites, p.armour, kind, sx, sy, TILE_PX);
      return;
    }
    // Fallback (sprites loading): owner-tinted square. Briefly visible on
    // first paint only, replaced by the real sprite within ~one frame.
    const half = TILE_PX * 0.4;
    ctx.fillStyle = ownerColor(p.owner);
    ctx.fillRect(sx - half, sy - half, half * 2, half * 2);
    ctx.strokeStyle = '#0a0e14';
    ctx.lineWidth = 1;
    ctx.strokeRect(sx - half + 0.5, sy - half + 0.5, half * 2 - 1, half * 2 - 1);
  }

  private drawBase(b: ClientBase, state: ClientState): void {
    const { sx, sy } = this.worldToScreen(
      (b.x << 8) + (WORLD_UNITS_PER_TILE >> 1),
      (b.y << 8) + (WORLD_UNITS_PER_TILE >> 1),
    );
    const ctx = this.mainCtx;
    const sprites = getSprites();
    // Original Bolo's good / neutral / evil base sprites: friendly (you +
    // mutual-pact allies), unowned, or hostile. NEUTRAL_OWNER (0xff) is the
    // BMAP "no owner yet" sentinel and gets the dedicated neutral sprite
    // (refuels everyone, capturable on contact).
    const kind: 'good' | 'neutral' | 'evil' =
      b.owner === 0xff ? 'neutral' : isFriendlyToMe(state, b.owner) ? 'good' : 'evil';
    if (sprites) {
      drawBaseSprite(ctx, sprites, kind, sx, sy, TILE_PX);
      return;
    }
    // Fallback (sprites loading): owner-tinted diamond.
    const half = TILE_PX * 0.5;
    ctx.fillStyle = ownerColor(b.owner);
    ctx.beginPath();
    ctx.moveTo(sx, sy - half);
    ctx.lineTo(sx + half, sy);
    ctx.lineTo(sx, sy + half);
    ctx.lineTo(sx - half, sy);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#0a0e14';
    ctx.stroke();
  }

  private drawBoat(boat: { x: number; y: number; dir: number }, ownerOfRider: number): void {
    const { sx, sy } = this.worldToScreen(boat.x, boat.y);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    const size = TILE_PX;
    if (sprites) {
      drawBoatSprite(ctx, sprites, ownerOfRider, boat.dir, sx, sy, size);
      // Local-player highlight ring while afloat.
      return;
    }
    // Fallback: synthesized raft.
    const half = TILE_PX * 0.55;
    const angleRad = (boat.dir / 256) * Math.PI * 2;
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate(angleRad);
    ctx.fillStyle = '#7a5a36';
    ctx.beginPath();
    ctx.moveTo(0, -half * 1.2);
    ctx.lineTo(half * 0.8, -half * 0.2);
    ctx.lineTo(half * 0.8, half * 0.9);
    ctx.lineTo(-half * 0.8, half * 0.9);
    ctx.lineTo(-half * 0.8, -half * 0.2);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#3a2a18';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }

  private drawLGM(tank: ClientTank, lerped?: { x: number; y: number } | null): void {
    const { sx, sy } = this.worldToScreen(lerped?.x ?? tank.lgmX, lerped?.y ?? tank.lgmY);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    if (sprites) {
      if (tank.lgmStatus === LGMStatus.Parachuting) {
        // Helicopter inbound from the map edge, dropping a fresh LGM.
        drawHelicopterSprite(ctx, sprites, sx, sy, TILE_PX);
      } else {
        // Walking / building — cycle the 3 LGM frames at ~6 Hz so movement
        // reads as motion. Tied to performance.now() so it animates even
        // when the LGM stands still in the Building state (idle bob).
        const frame = Math.floor(performance.now() / 167);
        drawLgmSprite(ctx, sprites, frame, sx, sy, TILE_PX);
      }
      return;
    }
    ctx.fillStyle = LGM_COLOR;
    ctx.beginPath();
    ctx.arc(sx, sy, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawGunsight(wx: number, wy: number, dir: number, sightLen: number): void {
    // sightLen is in HALF map-squares per Cheshire's Brain.h (`gunrange`).
    // Shell distance = sightLen/2 tiles (see shellTtlForSightLen in sim/shells.ts);
    // the reticle has to match that or it lies about where the shell will land.
    const v = dirVector(dir);
    const len = sightLen * (WORLD_UNITS_PER_TILE / 2);
    const gx = wx + v.dx * len;
    const gy = wy + v.dy * len;
    const { sx, sy } = this.worldToScreen(gx, gy);
    const ctx = this.mainCtx;
    const sprites = getSprites();
    if (sprites) {
      drawGunsightSprite(ctx, sprites, sx, sy, TILE_PX);
      return;
    }
    // Fallback crosshair so aiming is at least visible before sprites load.
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sx - 6, sy);
    ctx.lineTo(sx + 6, sy);
    ctx.moveTo(sx, sy - 6);
    ctx.lineTo(sx, sy + 6);
    ctx.stroke();
  }
}

void Terrain; // imported for cross-reference

// Tiny "BOT" badge drawn above autopilot tanks so they're distinguishable
// from human-driven tanks at a glance. Centred on (cx, cy). Background is
// tinted by personality so a crowded battlefield is readable: red for
// assassin, blue for defensive, etc.
const PERSONALITY_BADGE_BG: Record<BotPersonality, string> = {
  default: 'rgba(0,0,0,0.7)',
  assassin: 'rgba(180,30,40,0.85)',
  defensive: 'rgba(40,80,180,0.85)',
  pillhunter: 'rgba(180,140,30,0.85)',
  basehunter: 'rgba(40,150,80,0.85)',
};

function drawBotBadge(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  personality: BotPersonality | undefined,
): void {
  const w = 22;
  const h = 10;
  ctx.save();
  ctx.fillStyle = PERSONALITY_BADGE_BG[personality ?? 'default'];
  ctx.fillRect(cx - w / 2, cy - h / 2, w, h);
  ctx.fillStyle = '#fff';
  ctx.font = '8px ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('BOT', cx, cy);
  ctx.restore();
}
